'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { useUser } from '../../hooks/useUser';
import { Path } from '../../routes/components/path';
import { config } from '../../utils/config';
import { storage } from '../../utils/storage';
import { consumeEpassiMobileNonce, hasPendingEpassiMobileNonce } from '../../utils/epassi';
import { resumeAppsFlyerIfPaused } from '../../utils/analytics/appsflyerAnalytics';
import { subscribeEpassiMobileResult } from './epassiMobileResult';
import { useStoreEPassiWalletToken } from './useStoreEPassiWalletToken';
import { EPASSI_REDIRECT_KEY } from './useActivateEpassiWallet';

// Finishes the iOS Epassi deep-link flow: AppUrlListener recognizes the return URL
// via handleEpassiAppUrlOpen, which delivers it through the in-memory channel, and
// this globally mounted handler stores the token.
// Android does not use this path — its token resolves inline in EPassiLoginButton.
const EPassiMobileReturnHandler: React.FC = () => {
  const { user, isUserFetching } = useUser();
  const router = useRouter();
  const { storeToken, reportFailed } = useStoreEPassiWalletToken();

  // Lift a pending AppsFlyer pause on every way back into the app: cold start
  // (mount), returning to the foreground with or without a result (appStateChange),
  // and after a result is processed (below). By the time any of these run, iOS has
  // already delivered the return URL to the (stopped) SDK, so resuming cannot race it.
  // Gated on hasPendingEpassiMobileNonce so the native isSDKStopped bridge call only
  // happens while an Epassi round trip is actually pending, not on every app resume
  // for every user regardless of whether they've ever touched Epassi.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !config.epassiEnabled) return;

    if (hasPendingEpassiMobileNonce()) void resumeAppsFlyerIfPaused();

    let handle: PluginListenerHandle | null = null;
    let unmounted = false;

    void (async () => {
      const listener = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive && hasPendingEpassiMobileNonce()) void resumeAppsFlyerIfPaused();
      });
      if (unmounted) {
        void listener.remove();
        return;
      }
      handle = listener;
    })();

    return () => {
      unmounted = true;
      void handle?.remove();
    };
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !config.epassiEnabled) return;
    // Wait for the user query to settle so a cold-start return isn't misread
    // as "not logged in".
    if (isUserFetching) return;

    return subscribeEpassiMobileResult((result) => {
      void (async () => {
        await resumeAppsFlyerIfPaused();

        // Unsolicited returns (no live nonce — e.g. a forged URL opened from
        // another app) are dropped without feedback, mirroring the web flow.
        if (!consumeEpassiMobileNonce()) return;

        if (!user) {
          router.replace(Path.Login);
          return;
        }

        if (result.kind === 'token') {
          await storeToken({ walletToken: result.token });
        } else {
          reportFailed();
        }

        // Checkout stores its own return path before launching Epassi; account
        // settings sets none, so it falls back to today's behavior.
        const destination = storage.getItem(EPASSI_REDIRECT_KEY);
        storage.removeItem(EPASSI_REDIRECT_KEY);
        router.replace(destination ?? Path.PaymentMethods);
      })();
    });
  }, [isUserFetching, reportFailed, router, storeToken, user]);

  return null;
};

export default EPassiMobileReturnHandler;
